/**
 * shared/inbox-address.ts — the couple's shared inbox address (card qntf9Qa0Cemr).
 *
 * A couple's inbox is the two lane addresses below (see INBOX_LANE_ADDRESSES);
 * the mail that lands there is triaged by the concierge into their shared
 * space (card 17XyniccuSCt / 99VNKrT2nwvy). Every surface that tells a couple
 * where to email — the Admin inbox menu, Settings, and the invite moment —
 * builds the address from ONE domain so they can never disagree.
 *
 * pearconnected.io is the product domain as of the migration
 * (card 5hxgM3PswnC6, Jonathan 2026-10-20: the coded `<code>@` alias is
 * deprecated — only the `us@` and `me@` routes are needed). Mail to the
 * togetherconnect.io lanes keeps being received too (that zone's catch-all
 * is untouched), so couples who already shared the old address lose nothing.
 *
 * The CODE is server-owned: it is the 6-char invite code, recovered from the
 * server-issued coupleId (`coupleIdFromCode` = `c_` + code). Nothing here
 * mints a code — a surface only ever formats one the server already issued
 * (the rule from card i5T4k6aAQmam: never show a code the server has not
 * heard of). The DOMAIN is the product's fixed inbox host.
 */
export const INBOX_DOMAIN = 'pearconnected.io';

/**
 * The two lane addresses (card QLPMbGu3EiXm) — the SAME for every couple, so
 * there is no code for anyone to copy, mistype or remember. Which couple a
 * message belongs to is resolved from its sender, not its recipient.
 *
 *   me@  private to whoever sent it
 *   us@  shared by the couple
 */
export const INBOX_LANE_ADDRESSES = {
  me: `me@${INBOX_DOMAIN}`,
  us: `us@${INBOX_DOMAIN}`,
} as const;

export type InboxLaneKey = keyof typeof INBOX_LANE_ADDRESSES;

/** The private address — for a message only its sender should read. */
export function meAddress(): string {
  return INBOX_LANE_ADDRESSES.me;
}

/** The shared address — for a message that lands with you both. */
export function usAddress(): string {
  return INBOX_LANE_ADDRESSES.us;
}

/**
 * The inbox address for a couple code. The code is the 6-char invite code;
 * the domain is the shared inbox host.
 *
 * Legacy: this is the coded couple address that predates the lane addresses.
 * It still resolves, and nothing in the UI points at it any more.
 */
export function inboxAddress(code: string): string {
  return `${code}@${INBOX_DOMAIN}`;
}

/**
 * Recover the 6-char couple code from a server-issued coupleId. `coupleId` is
 * always `coupleIdFromCode(code)` = `c_<code>`, so the inverse is a prefix
 * strip. Returns null for anything that is not a couple-shaped id — a surface
 * then renders nothing rather than a malformed address.
 */
export function coupleCodeFromId(coupleId: string | null | undefined): string | null {
  if (!coupleId || !coupleId.startsWith('c_')) return null;
  const code = coupleId.slice(2);
  return code.length > 0 ? code : null;
}
